import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pathById, pieceById } from '../../data';
import type { HoldOption, HoldSpec, Moment, PieceData } from '../../data/types';
import { alteredBars as alteredBarsOf } from '../../music/alterations';
import { Layer, blockChord, preludeFiguration } from '../../music/figuration';
import { figuredBass } from '../../music/figures';
import { keyName, parseKey } from '../../music/key';
import { Measure, barAt, barEnd, barStart } from '../../music/piece';
import { parsePitch } from '../../music/pitch';
import { formatRoman, realizeRoman } from '../../music/roman';
import { chordTension, WEIGHTS } from '../../music/tension';
import { midiOf } from '../../music/pitch';
import { Player } from '../../audio/player';
import { DEPTH_LABELS, LAYER_LABELS } from '../copy';
import { CORPUS, corpusEntry } from '../../data/chorales/corpusIndex';
import { TUNE_NOTES } from '../../data/chorales/editorial';
import { shortcutBlocked, usePlayer, usePrefs, useWidth } from '../hooks';
import { buildView, eventAt, layerNotes, layoutTerrain, mapTime, regionAt, toPlayNotes } from '../model';
import { GravityScore } from '../components/GravityScore';
import { ScoreStrip } from '../components/ScoreStrip';
import { EngravedStrip } from '../components/EngravedStrip';
import { engrave } from '../engrave/layout';
import { alignedNotation } from '../../data/notation';
import { Well } from '../components/Well';
import { Threads } from '../components/Threads';
import { SlidersIcon } from '../components/Icons';
import { ArrowLeft, CloseIcon, FlipIcon, HelpIcon, HoldIcon, LoopIcon, PauseIcon, PlayIcon, RestartIcon } from '../components/Icons';
import { takePlayRequest } from '../intent';

type Mode = 'fog' | 'map';
type HoldStage = 'playing' | 'choosing' | 'revealing' | 'revealed';

/** Start and end of a moment in quarters (moments may begin inside a bar). */
export function momentSpan(m: Moment, measures: Measure[]): { start: number; end: number } {
  const first = measures.find((x) => x.bar >= m.from) ?? measures[0];
  const last = [...measures].reverse().find((x) => x.bar <= m.to) ?? measures[measures.length - 1];
  return { start: m.start ?? first.start, end: m.end ?? last.start + last.length };
}

/** When a hold stops, where the lead-in starts, and how long the composer's answer lasts. */
function holdTimes(h: HoldSpec, m: Moment, measures: Measure[]) {
  const stop = h.at ?? barEnd(measures, h.afterBar!);
  const span = momentSpan(m, measures);
  const from = Math.max(span.start, stop - 12);
  const revealFrom = h.at !== undefined ? Math.max(span.start, stop - 4) : barStart(measures, h.afterBar!);
  const resolveEnd = h.at !== undefined ? stop + (h.resolveLength ?? 2) : barEnd(measures, h.afterBar! + 1);
  return { stop, from, revealFrom, resolveEnd };
}

function optionNotes(o: HoldOption, bar: number) {
  if (o.voicing) return { notes: preludeFiguration(o.voicing, 0, bar), length: 4 };
  const dur = o.chord?.dur ?? 2;
  return { notes: blockChord(o.chord?.notes ?? [], 0, dur, bar), length: dur };
}

export function Journey({ pieceId, momentId }: { pieceId: string; momentId?: string }) {
  const piece = pieceById(pieceId);
  if (!piece) {
    return (
      <div className="page-head">
        <h1>Not in the atlas yet</h1>
        <p>
          There is no piece called “{pieceId}”. <a href="#bwv846">Open the Prelude in C</a>.
        </p>
      </div>
    );
  }
  return <JourneyInner key={piece.id} piece={piece} initialMoment={momentId} />;
}

function JourneyInner({ piece, initialMoment }: { piece: PieceData; initialMoment?: string }) {
  const { prefs, setPrefs } = usePrefs();
  const { depth, lens } = prefs;
  const [mode, setMode] = useState<Mode>(initialMoment ? 'map' : 'fog');
  const [pinned, setPinned] = useState<string | null>(initialMoment ?? null);
  const [whatIfId, setWhatIfId] = useState<string | null>(null);
  const [listenAlt, setListenAlt] = useState(false);
  const [layer, setLayer] = useState<Layer>('surface');
  const [loop, setLoop] = useState<{ start: number; end: number } | null>(null);
  const [flipped, setFlipped] = useState(depth >= 4);
  const [hold, setHold] = useState<{ momentId: string; stage: HoldStage; chosen?: string } | null>(null);
  const [help, setHelp] = useState(false);
  const [scoreRef, scoreWidth] = useWidth<HTMLDivElement>();
  const [sideRef, sideWidth] = useWidth<HTMLDivElement>();
  // The live dock: transport, moving score and tension band, pinned under the header while you scroll.
  const [dockRef, dockWidth] = useWidth<HTMLDivElement>();
  const dockSentinel = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const el = dockSentinel.current;
      if (!el) return;
      // Compact once the page has scrolled well past the dock's own place.
      setCompact(el.getBoundingClientRect().top < -140);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    check();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // Its height, for anything that scrolls into view under it (a moment card, a jump target).
  useEffect(() => {
    const el = dockRef.current;
    if (!el) return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--dock-h', `${Math.round(el.getBoundingClientRect().height)}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--dock-h');
    };
  }, [dockRef]);

  const { player, position, playing } = usePlayer();
  const optionPlayer = useMemo(() => new Player(), []);
  useEffect(() => () => optionPlayer.dispose(), [optionPlayer]);

  const view = useMemo(() => buildView(piece), [piece]);
  const tempoScale = piece.kind === 'sonata' || piece.kind === 'variations' || piece.kind === 'prelude' ? piece.tempo / 66 : 1;
  const allWhatIfs = useMemo(() => piece.moments.flatMap((m) => m.whatIfs.map((w) => ({ moment: m, whatIf: w }))), [piece]);
  const activeWhatIf = allWhatIfs.find((x) => x.whatIf.id === whatIfId) ?? null;
  const alt = useMemo(() => (activeWhatIf ? buildView(piece, activeWhatIf.whatIf) : null), [piece, activeWhatIf]);
  const playingView = listenAlt && alt ? alt : view;

  // Notes to the player whenever the version or the layer changes. A pending seek
  // (from an A/B switch) is applied right after the new notes are in place.
  const layered = useMemo(() => layerNotes(playingView, layer), [playingView, layer]);
  const playNotes = useMemo(() => toPlayNotes(layered), [layered]);
  const pendingSeek = useRef<number | null>(null);
  const pendingPlay = useRef(false);
  useEffect(() => {
    player.setNotes(playNotes, playingView.length);
    if (pendingSeek.current !== null) {
      player.seek(pendingSeek.current);
      pendingSeek.current = null;
    }
    if (pendingPlay.current) {
      pendingPlay.current = false;
      void player.play();
    }
  }, [player, playNotes, playingView.length]);
  useEffect(() => {
    // Sonata movements carry their own speed; the slider scales it (66 on the slider = as marked).
    const bpm = prefs.tempo * tempoScale;
    player.setTempo(bpm);
    optionPlayer.setTempo(bpm);
  }, [player, optionPlayer, prefs.tempo, tempoScale]);
  useEffect(() => player.setTempoMap(piece.tempoMap), [player, piece.tempoMap]);

  // Arriving from a "Listen" link: start playing at once (the link unlocked the audio). A link to
  // a moment plays that moment and stops at its end.
  const arrivalStop = useRef(false);
  useEffect(() => {
    const req = takePlayRequest(piece.id);
    if (!req) return;
    const m = req.momentId ? piece.moments.find((x) => x.id === req.momentId) : undefined;
    if (m) {
      const span = momentSpan(m, piece.measures);
      arrivalStop.current = true;
      player.setHold(span.end);
      void player.play(span.start);
    } else void player.play(0);
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Display position is always in the original timeline.
  const displayPos = listenAlt && alt ? mapTime(alt.core.measures, view.core.measures, position) : position;
  const bar = barAt(view.core.measures, displayPos);
  const autoMoment =
    piece.moments.find((m) => {
      const s = momentSpan(m, view.core.measures);
      return displayPos >= s.start - 1e-6 && displayPos < s.end - 1e-6;
    }) ??
    [...piece.moments].reverse().find((m) => momentSpan(m, view.core.measures).start <= displayPos) ??
    piece.moments[0];
  // A running what-if keeps its moment on screen; otherwise a pinned moment, otherwise the one under the playhead.
  const moment = activeWhatIf?.moment ?? ((pinned && piece.moments.find((m) => m.id === pinned)) || autoMoment);
  const currentEvent = eventAt(playingView.events, position);
  const region = regionAt(playingView.regions, currentEvent?.bar ?? 1, position);

  // Following a link to a moment inside the same piece.
  useEffect(() => {
    if (!initialMoment) return;
    const m = piece.moments.find((x) => x.id === initialMoment);
    if (!m) return;
    setPinned(m.id);
    setMode('map');
    player.seek(momentSpan(m, piece.measures).start);
  }, [initialMoment, piece, player]);

  // Guess what comes next (stop before a resolution)
  useEffect(() => {
    player.onStop = (reason) => {
      if (reason === 'hold' && arrivalStop.current) {
        arrivalStop.current = false;
        player.setHold(null);
        return;
      }
      if (reason === 'hold') setHold((h) => (h ? { ...h, stage: h.stage === 'revealing' ? 'revealed' : 'choosing' } : h));
    };
    return () => {
      player.onStop = null;
    };
  }, [player]);

  const startHold = useCallback(
    (m: Moment) => {
      if (!m.hold) return;
      setWhatIfId(null);
      setListenAlt(false);
      setLoop(null);
      player.setLoop(null);
      const t = holdTimes(m.hold, m, view.core.measures);
      player.setHold(t.stop);
      setHold({ momentId: m.id, stage: 'playing' });
      setPinned(m.id);
      void player.play(t.from);
    },
    [player, view],
  );

  const playOption = useCallback(
    (m: Moment, o: HoldOption) => {
      if (!m.hold) return;
      const { notes, length } = optionNotes(o, m.to);
      optionPlayer.setNotes(toPlayNotes(notes), length);
      void optionPlayer.play(0);
      setHold((h) => (h ? { ...h, chosen: o.id } : h));
    },
    [optionPlayer],
  );

  const revealHold = useCallback(
    (m: Moment) => {
      if (!m.hold) return;
      optionPlayer.pause();
      const t = holdTimes(m.hold, m, view.core.measures);
      player.setHold(t.resolveEnd);
      setHold((h) => (h ? { ...h, stage: 'revealing' } : h));
      void player.play(t.revealFrom);
    },
    [optionPlayer, player, view],
  );

  const endHold = useCallback(() => {
    player.setHold(null);
    setHold(null);
  }, [player]);

  // What-if A/B: switch versions at the same musical place.
  const chooseWhatIf = (id: string | null) => {
    if (hold) endHold();
    setWhatIfId(id);
    setListenAlt(!!id);
    setLoop(null);
    player.setLoop(null);
    if (id) {
      const target = allWhatIfs.find((x) => x.whatIf.id === id)!;
      const altView = buildView(piece, target.whatIf);
      // Playing: switch in place. Paused: play the changed passage from the start of its moment.
      const span = momentSpan(target.moment, view.core.measures);
      const inside = displayPos >= span.start - 1e-6 && displayPos < span.end - 1e-6;
      const from = playing && inside ? displayPos : span.start;
      pendingSeek.current = mapTime(view.core.measures, altView.core.measures, from);
      if (!playing) pendingPlay.current = true;
    } else {
      pendingSeek.current = displayPos;
    }
  };
  const switchAB = (toAlt: boolean) => {
    if (!alt || toAlt === listenAlt) return;
    const mapped = toAlt ? mapTime(view.core.measures, alt.core.measures, position) : mapTime(alt.core.measures, view.core.measures, position);
    setListenAlt(toAlt);
    if (loop) {
      const from = toAlt ? view.core.measures : alt.core.measures;
      const to = toAlt ? alt.core.measures : view.core.measures;
      const l = { start: mapTime(from, to, loop.start), end: mapTime(from, to, loop.end - 0.001) + 0.001 };
      setLoop(l);
    }
    pendingSeek.current = mapped;
  };
  useEffect(() => {
    player.setLoop(loop);
  }, [player, loop]);

  const togglePlay = useCallback(() => {
    if (playing) {
      player.pause();
      return;
    }
    if (arrivalStop.current) {
      arrivalStop.current = false;
      player.setHold(null);
    }
    if (hold) {
      optionPlayer.pause();
      player.setHold(null);
      setHold(null);
    }
    void player.play();
  }, [player, optionPlayer, playing, hold]);

  // Choosing another moment leaves a what-if or a guess that belongs to the one on screen.
  const goToMoment = (m: Moment) => {
    setPinned(m.id);
    if (hold && hold.momentId !== m.id) endHold();
    if (activeWhatIf && activeWhatIf.moment.id !== m.id) {
      setWhatIfId(null);
      setListenAlt(false);
      setLoop(null);
      player.setLoop(null);
      // The written version takes over: seek once its notes are in (at once, if they already are).
      const at = momentSpan(m, view.core.measures).start;
      if (listenAlt) pendingSeek.current = at;
      else player.seek(at);
      return;
    }
    player.seek(momentSpan(m, playingView.core.measures).start);
  };

  const loopMoment = (m: Moment) => {
    const range = momentSpan(m, playingView.core.measures);
    const same = loop && Math.abs(loop.start - range.start) < 1e-6 && Math.abs(loop.end - range.end) < 1e-6;
    setLoop(same ? null : range);
    if (!same) {
      setPinned(m.id);
      void player.play(range.start);
    }
  };

  // Keyboard: space plays, F toggles fog, arrows move by bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (shortcutBlocked(e)) return;
      if (e.key === ' ' && tag !== 'BUTTON' && tag !== 'A') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'f' || e.key === 'F') {
        setMode((m) => (m === 'fog' ? 'map' : 'fog'));
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const ms = playingView.core.measures;
        const b = barAt(ms, position);
        const i = ms.findIndex((m) => m.bar === b) + (e.key === 'ArrowRight' ? 1 : -1);
        if (i >= 0 && i < ms.length) player.seek(ms[i].start);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, player, playingView, position]);

  const alteredBars = activeWhatIf ? alteredBarsOf(activeWhatIf.whatIf.ops) : [];
  const fog = mode === 'fog';

  // Terrain nodes: chords of the current region, in its key.
  const regionKey = region?.key ?? piece.homeKey;
  const terrain = useMemo(() => {
    const k = parseKey(regionKey);
    const inRegion = playingView.events.filter(
      (ev) =>
        region &&
        ev.spec.key === regionKey &&
        (region.start !== undefined ? ev.start >= region.start - 1e-6 && ev.start < (region.end ?? Infinity) : ev.bar >= region.from && ev.bar <= region.to),
    );
    const symbols = [...new Set(inRegion.map((ev) => ev.spec.rn))];
    if (!symbols.length && currentEvent) symbols.push(currentEvent.spec.rn);
    return layoutTerrain(symbols, k);
  }, [playingView, region, regionKey, currentEvent]);
  const trail = useMemo(() => {
    if (!currentEvent) return [];
    const i = currentEvent.index;
    return playingView.events
      .slice(Math.max(0, i - 4), i + 1)
      .filter((ev) => ev.spec.key === regionKey)
      .map((ev) => ev.spec.rn);
  }, [currentEvent, playingView, regionKey]);

  const currentTension = currentEvent ? playingView.tension.perEvent[currentEvent.index] : null;

  const lastBar = piece.measures[piece.measures.length - 1].bar;
  const firstBar = piece.measures[0];
  const pickup = bar === firstBar.bar && firstBar.bar === 0;
  const where = pickup ? `Upbeat, before bar 1 of ${lastBar}` : `Bar ${bar} of ${lastBar}`;
  // While a guess waits for an answer, the player names the passage being guessed, not the answer.
  const nowMoment = hold ? moment : autoMoment;
  // In the fog a derived line's feeling would name how it ends before it is heard: say which line it is instead.
  const nowName =
    depth === 0 ? '' : depth === 1 && !(fog && piece.kind !== 'keyboard') ? nowMoment.feeling : nowMoment.theoryTitle.split(':')[0];
  // The edition's notation, when it lines up with the playback (and the music heard is the written music).
  const engraving = useMemo(() => {
    const n = alignedNotation(piece.id, piece.measures, piece.notes);
    return n ? engrave(n, { satb: piece.kind === 'chorale' }) : null;
  }, [piece]);
  const engravedNow = engraving && layer === 'surface' && !(listenAlt && alt);
  const phone = dockWidth > 0 && dockWidth < 640;
  const staffSpace = phone ? (compact ? 4.4 : 5.2) : compact ? 5.6 : 6.6;
  const bandH = phone ? (compact ? 10 : 14) : compact ? 16 : 22;

  return (
    <div className="journey">
      <div className="journey-head">
        <div>
          <a href="#library" className="btn quiet" style={{ marginLeft: -12, marginBottom: 6 }}>
            <ArrowLeft /> All pieces
          </a>
          <h1>{piece.title}</h1>
          <div className="byline">
            {piece.composer} · {piece.catalogue} · {piece.collection}
            {piece.year ? `, ${piece.year}` : ''}
            {piece.subtitle ? <span className="muted"> · {piece.subtitle}</span> : null}
          </div>
          <TuneLink pieceId={piece.id} />
          {CORPUS.forms.some((f) => f.id === piece.id) && (
            <a className="chip tune-link" href={`#form.${piece.id}`}>
              {depth >= 2 ? 'Exposition and recapitulation, side by side →' : 'Hear the return next to the first time →'}
            </a>
          )}
          {CORPUS.returns.some((r) => r.id === piece.id) && (
            <a className="chip tune-link" href={`#returns.${piece.id}`}>
              {CORPUS.returns.find((r) => r.id === piece.id)!.kind === 'variations' ? 'The theme and every variation, side by side →' : 'Every return of the refrain, side by side →'}
            </a>
          )}
        </div>
        <div className="head-tools">
          <div className="seg" role="group" aria-label="View">
            <button aria-pressed={fog} onClick={() => setMode('fog')} title="Hear it in time: only the line of tension is drawn, as it plays (F)">
              Listen
            </button>
            <button aria-pressed={!fog} onClick={() => setMode('map')} title="See the whole piece at once: keys, cadences and patterns (F)">
              Map
            </button>
          </div>
          <button className="btn quiet icon" aria-expanded={help} onClick={() => setHelp((h) => !h)} aria-label="How this page works" title="How this page works">
            <HelpIcon />
          </button>
        </div>
      </div>
      {help && <PageHelp onClose={() => setHelp(false)} />}

      <div ref={dockSentinel} aria-hidden="true" />
      <div className="live-dock" data-compact={compact} ref={dockRef}>
        <div className="transport" role="toolbar" aria-label="Playback">
          <button className="play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button className="btn quiet icon" onClick={() => player.seek(loop?.start ?? 0)} aria-label="Back to the start" title="Back to the start">
            <RestartIcon />
          </button>
          <span className="now" aria-live="off">
            <span className="where">{where}</span>
            {nowName && <span className="now-name">{listenAlt && activeWhatIf ? `What if: ${activeWhatIf.whatIf.label}` : nowName}</span>}
          </span>
          {/* The moment's name, announced when it changes (not every bar). */}
          <span className="sr-only" aria-live="polite">
            {playing ? nowName : ''}
          </span>
          {loop && (
            <button className="btn accent" aria-pressed onClick={() => setLoop(null)} title="Stop looping">
              <LoopIcon /> <span className="wide-only">Stop loop</span>
            </button>
          )}
          <span className="grow" />
          <span className="label hear-label wide-only">Hear</span>
          <div className="seg wide-only" role="group" aria-label="What you hear">
            {(Object.keys(LAYER_LABELS) as Layer[]).map((l) => (
              <button key={l} aria-pressed={layer === l} onClick={() => setLayer(l)} title={LAYER_LABELS[l].hint}>
                {LAYER_LABELS[l].label}
              </button>
            ))}
          </div>
          <label className="field wide-only">
            Tempo
            <input
              id="tempo"
              type="range"
              min={40}
              max={110}
              value={prefs.tempo}
              onChange={(e) => setPrefs({ tempo: Number(e.target.value) })}
              aria-valuetext={`${Math.round(prefs.tempo * tempoScale)} beats per minute`}
            />
          </label>
          <button
            className="btn quiet icon dock-toggle"
            aria-label="Moving score"
            aria-pressed={prefs.score}
            onClick={() => setPrefs({ score: !prefs.score })}
            title={prefs.score ? 'Hide the moving score' : 'Show the moving score'}
          >
            <ScoreGlyph />
          </button>
          <button className="btn quiet icon narrow-only" aria-expanded={soundOpen} aria-controls="dock-sound" onClick={() => setSoundOpen((o) => !o)} aria-label="What you hear and tempo" title="What you hear and tempo">
            <SlidersIcon />
          </button>
        </div>
        {soundOpen && (
          <div className="transport-sub narrow-only" id="dock-sound">
            <label className="field">
              Hear
              <select id="layer" value={layer} onChange={(e) => setLayer(e.target.value as Layer)}>
                {(Object.keys(LAYER_LABELS) as Layer[]).map((l) => (
                  <option key={l} value={l}>
                    {LAYER_LABELS[l].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Tempo
              <input
                id="tempo-narrow"
                type="range"
                min={40}
                max={110}
                value={prefs.tempo}
                onChange={(e) => setPrefs({ tempo: Number(e.target.value) })}
                aria-valuetext={`${Math.round(prefs.tempo * tempoScale)} beats per minute`}
              />
            </label>
          </div>
        )}
        {prefs.score && dockWidth > 0 && (
          <div className="score-strip">
            {engravedNow ? (
              <EngravedStrip
                eng={engraving}
                measures={playingView.core.measures}
                view={playingView}
                position={position}
                width={dockWidth - 2}
                space={staffSpace}
                fog={fog}
                band={bandH}
                label={`Score of ${piece.title}, ${where.toLowerCase()}. Drag to move through the music.`}
                onSeek={(t) => {
                  player.seek(t);
                  setPinned(null);
                }}
              />
            ) : (
            <ScoreStrip
              notes={layered}
              measures={playingView.core.measures}
              view={playingView}
              homeKey={piece.homeKey}
              position={position}
              width={dockWidth - 2}
              space={staffSpace}
              fog={fog}
              satb={piece.kind === 'chorale'}
              band={bandH}
              label={`Score of ${piece.title}, ${where.toLowerCase()}. Drag to move through the music.`}
              onSeek={(t) => {
                player.seek(t);
                setPinned(null);
              }}
            />
            )}
          </div>
        )}
      </div>

      <div className="score" ref={scoreRef}>
        {scoreWidth > 0 && (
          <GravityScore
            piece={piece}
            view={view}
            alt={alt}
            width={scoreWidth}
            position={displayPos}
            fog={fog}
            depth={depth}
            lens={lens}
            moment={fog ? null : moment}
            loop={loop && listenAlt && alt ? { start: mapTime(alt.core.measures, view.core.measures, loop.start), end: mapTime(alt.core.measures, view.core.measures, loop.end - 0.001) } : loop}
            alteredBars={alteredBars}
            onSeek={(t) => {
              const mapped = listenAlt && alt ? mapTime(view.core.measures, alt.core.measures, t) : t;
              player.seek(mapped);
              setPinned(null);
            }}
            onLoop={(r) => {
              if (!r) return setLoop(null);
              const mapped = listenAlt && alt ? { start: mapTime(view.core.measures, alt.core.measures, r.start), end: mapTime(view.core.measures, alt.core.measures, r.end - 0.001) + 0.001 } : r;
              setLoop(mapped);
              void player.play(mapped.start);
            }}
          />
        )}
        {fog && displayPos === 0 && !playing && (
          <p className="fog-hint">
            Press <b>play</b> and listen. The line rises when the harmony pulls away from home and falls when it comes to rest.
          </p>
        )}
      </div>

      {!fog && <MapLegend depth={depth} sections={!!piece.sections?.length} />}

      {!fog && (
        <nav className="chips moments-nav" aria-label="Moments" style={{ marginTop: 12 }}>
          {piece.moments.map((m) => (
            <button
              key={m.id}
              className="chip"
              aria-pressed={moment.id === m.id}
              style={moment.id === m.id ? { borderColor: 'var(--ink-2)', background: 'var(--raised)' } : undefined}
              onClick={() => goToMoment(m)}
            >
              <span className="mono" style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>
                {m.from}–{m.to}
              </span>
              {depth === 0 ? '' : depth === 1 ? m.feeling : m.theoryTitle.split(':')[0]}
            </button>
          ))}
        </nav>
      )}

      <div className="journey-grid">
        <MomentCard
          piece={piece}
          moment={moment}
          depth={depth}
          lens={lens}
          flipped={flipped || depth >= 4}
          onFlip={() => setFlipped((f) => !f)}
          loopOn={!!loop}
          onLoop={() => loopMoment(moment)}
          whatIfId={whatIfId}
          onWhatIf={chooseWhatIf}
          listenAlt={listenAlt}
          onAB={switchAB}
          hold={hold && hold.momentId === moment.id ? hold : null}
          onHold={() => startHold(moment)}
          onOption={(o) => playOption(moment, o)}
          onReveal={() => revealHold(moment)}
          onEndHold={() => {
            endHold();
            void player.play(barStart(view.core.measures, (moment.hold?.afterBar ?? moment.to) + 1));
          }}
          currentBar={bar}
          view={playingView}
        />

        <div className="panel terrain" ref={sideRef}>
          {fog ? (
            <div>
              <div className="panel-head">
                <span className="label">The map</span>
              </div>
              <p className="muted" style={{ fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '1.15rem' }}>
                The map stays hidden while you listen.
              </p>
              <p className="muted">When you are ready, open the map to see the whole piece at once: where it pulled, where it rested, which keys it visited and which patterns it used.</p>
              <button className="btn" onClick={() => setMode('map')}>
                Show the map
              </button>
            </div>
          ) : (
            <>
              <div className="panel-head">
                <span className="label">Gravity well{depth >= 2 ? ` · ${keyName(parseKey(regionKey))}` : ''}</span>
                {currentEvent && depth >= 3 && <span style={{ fontFamily: 'var(--display)', fontSize: '1.1rem' }}>{formatRoman(currentEvent.spec.rn)}</span>}
              </div>
              <Well keyToken={regionKey} nodes={terrain} current={currentEvent?.spec.rn} trail={trail} depth={depth} />
              {depth <= 1 && (
                <p className="well-note">
                  Each circle is a chord of this passage; the lit one is sounding now.{' '}
                  <button className="linklike" onClick={() => setPrefs({ depth: 2 })}>
                    Show chord names
                  </button>
                </p>
              )}
              {currentTension && depth >= 4 && <TensionParts parts={currentTension.parts} value={currentTension.value} />}
              {sideWidth > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div className="panel-head">
                    <span className="label">Voices{layer !== 'surface' ? ` · ${LAYER_LABELS[layer].label.toLowerCase()}` : ''}</span>
                    {depth >= 1 && <span className="muted" style={{ fontSize: '0.8rem' }}>Orange marks a note that rubs against its chord.</span>}
                  </div>
                  <Threads notes={layered} events={playingView.events} measures={playingView.core.measures} position={position} width={Math.max(200, sideWidth - 40)} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <footer className="foot">
        <p>
          {piece.editorial} Sources: {piece.sources.map((s, i) => (
            <span key={i}>
              {i > 0 ? '; ' : ''}
              {s.url ? <a href={s.url}>{s.label}</a> : s.label}
              {s.licence ? ` (${s.licence})` : ''}
            </span>
          ))}
          . Keys: space to play, F to switch between Listen and Map, ← → to move by bar. Theory shown: {DEPTH_LABELS[depth].short}.
        </p>
      </footer>
    </div>
  );
}

function TensionParts({ parts, value }: { parts: { fn: number; dissonance: number; distance: number; colour: number; pedal: number }; value: number }) {
  const rows: Array<[string, number]> = [
    ['function', parts.fn],
    ['dissonance', parts.dissonance],
    ['key distance', parts.distance],
    ['colour', parts.colour],
    ['pedal', parts.pedal],
  ];
  return (
    <div className="parts" aria-label="Tension model breakdown">
      {rows.map(([name, v]) => (
        <div key={name}>
          <span>{name}</span>
          <i style={{ width: `${Math.max(0, v) * 180}%`, background: v < 0 ? 'var(--rest)' : undefined }} />
          <em>{v.toFixed(2)}</em>
        </div>
      ))}
      <div>
        <span style={{ color: 'var(--ink)' }}>tension</span>
        <i style={{ width: `${value * 100}%` }} />
        <em>{value.toFixed(2)}</em>
      </div>
      <p className="muted" style={{ fontSize: '0.75rem', margin: 0 }}>
        Weights: function {WEIGHTS.fn}, dissonance {WEIGHTS.dissonance}. <a href="#method">How tension is computed</a>
      </p>
    </div>
  );
}

interface CardProps {
  piece: PieceData;
  moment: Moment;
  depth: number;
  lens: 'roman' | 'figured';
  flipped: boolean;
  onFlip: () => void;
  loopOn: boolean;
  onLoop: () => void;
  whatIfId: string | null;
  onWhatIf: (id: string | null) => void;
  listenAlt: boolean;
  onAB: (alt: boolean) => void;
  hold: { stage: HoldStage; chosen?: string } | null;
  onHold: () => void;
  onOption: (o: HoldOption) => void;
  onReveal: () => void;
  onEndHold: () => void;
  currentBar: number;
  view: ReturnType<typeof buildView>;
}

function MomentCard(p: CardProps) {
  const { moment, depth, piece } = p;
  const active = moment.whatIfs.find((w) => w.id === p.whatIfId) ?? null;
  const events = p.view.events.filter((ev) => ev.bar >= moment.from && ev.bar <= moment.to);
  const showWords = depth >= 1;
  return (
    <article className="panel moment" aria-live="polite">
      <div className="panel-head">
        <span className="bars">
          Bars {moment.from}–{moment.to}
        </span>
        {depth >= 1 && (
          <button className="btn quiet" onClick={p.onFlip} aria-expanded={p.flipped}>
            <FlipIcon /> {p.flipped ? 'Hide the theory' : 'Show the theory'}
          </button>
        )}
      </div>
      {showWords && <h2>{moment.feeling}</h2>}
      {moment.text && <p className="sung">“{moment.text}”</p>}
      {showWords && <p className="listen">{moment.listenFor}</p>}

      <div className="actions">
        <button className="btn" aria-pressed={p.loopOn} onClick={p.onLoop} title="Play this passage over and over">
          <LoopIcon /> {p.loopOn ? 'Stop loop' : `Loop bars ${moment.from}–${moment.to}`}
        </button>
        {moment.whatIfs.map((w) => (
          <button key={w.id} className="btn accent" aria-pressed={p.whatIfId === w.id} onClick={() => p.onWhatIf(p.whatIfId === w.id ? null : w.id)} title={w.question}>
            What if… {w.label.toLowerCase()}
          </button>
        ))}
        {moment.hold && !p.hold && (
          <button className="btn" onClick={p.onHold} title="The music stops just before it resolves. Choose what comes next, then hear what the composer wrote.">
            <HoldIcon /> Guess what comes next
          </button>
        )}
      </div>

      {active && (
        <section className="whatif" aria-label="What if">
          <h3>{active.question}</h3>
          <p>{active.effect}</p>
          <div className="ab" role="group" aria-label="Compare">
            <button aria-pressed={!p.listenAlt} onClick={() => p.onAB(false)}>
              As {piece.composer.split(' ').pop()} wrote it
            </button>
            <button aria-pressed={p.listenAlt} onClick={() => p.onAB(true)}>
              Changed
            </button>
          </div>
          <p className="muted" style={{ fontSize: '0.8rem', margin: '10px 0 0' }}>
            Switch between the two while it plays; the music keeps its place. The dashed line is the changed version’s tension.
          </p>
        </section>
      )}

      {moment.hold && p.hold && (
        <section className="hold" aria-label="Guess what comes next">
          {p.hold.stage === 'playing' && <p style={{ margin: 0 }}>Listen: the music will stop just before it resolves…</p>}
          {p.hold.stage !== 'playing' && (
            <>
              {p.hold.stage === 'revealed' || p.hold.stage === 'revealing' ? (
                <p className="hold-result">
                  {(() => {
                    const chosen = moment.hold!.options.find((o) => o.id === p.hold!.chosen);
                    const real = moment.hold!.options.find((o) => o.composer);
                    const who = piece.composer.split(' ').pop();
                    if (!real) return null;
                    if (!chosen) return `${who} chose: ${real.label}.`;
                    return chosen.id === real.id ? `You chose what ${who} wrote: ${real.label}.` : `You chose ${chosen.label}; ${who} wrote ${real.label}.`;
                  })()}
                </p>
              ) : (
                <p style={{ margin: 0, fontWeight: 500 }}>{moment.hold.prompt}</p>
              )}
              <div className="options">
                {moment.hold.options.map((o) => {
                  const chord = realizeRoman(o.rn, parseKey(o.key));
                  const pitches = o.voicing ?? o.chord?.notes ?? [];
                  const t = chordTension({ chord, sounding: pitches.map((n) => midiOf(parsePitch(n))), regionKey: parseKey(o.key), homeKey: parseKey(piece.homeKey) }).value;
                  const revealed = p.hold!.stage === 'revealed' || p.hold!.stage === 'revealing';
                  return (
                    <button key={o.id} className="option" onClick={() => p.onOption(o)} data-composer={!!o.composer} data-revealed={revealed} aria-pressed={p.hold!.chosen === o.id}>
                      <b>{depth >= 1 ? o.label : 'Option'}</b>
                      <span>
                        {o.hint}
                        {depth >= 3 ? ` · ${formatRoman(o.rn)}` : ''}
                        {revealed && o.composer ? ` · ${piece.composer.split(' ').pop()}’s choice` : ''}
                      </span>
                      <span className="meter" aria-hidden="true">
                        <i style={{ width: `${Math.round(t * 100)}%` }} />
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="actions" style={{ marginTop: 0 }}>
                <button className="btn" onClick={p.onReveal}>
                  Hear what {piece.composer.split(' ').pop()} wrote
                </button>
                <button className="btn quiet" onClick={p.onEndHold}>
                  Carry on listening
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {p.flipped && (
        <section className="back" aria-label="Names and theory">
          <span className="label">What it is called</span>
          <h3>{moment.theoryTitle}</h3>
          <p>{moment.theory}</p>
          {depth >= 2 && (
            <div className="chord-list">
              {events.map((ev) => {
                const uniq = [...new Map(ev.sounding.map((n) => [n.midi, n])).values()].sort((a, b) => a.midi - b.midi);
                const figs = figuredBass(uniq.map((n) => parsePitch(n.name)), ev.key).join(' ');
                return (
                  <div key={ev.index} data-current={ev.bar === p.currentBar}>
                    <span>bar {ev.bar}</span>
                    <b>{p.lens === 'figured' ? figs || '5/3' : formatRoman(ev.spec.rn)}</b>
                    <span>
                      {ev.spec.key}
                      {ev.spec.pivot ? ` = ${ev.spec.pivot.key}: ${formatRoman(ev.spec.pivot.rn)}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {depth >= 4 && events.some((ev) => ev.spec.note) && (
            <ul className="muted" style={{ fontSize: '0.86rem', paddingLeft: '1.1em' }}>
              {events
                .filter((ev) => ev.spec.note)
                .map((ev) => (
                  <li key={ev.index}>
                    Bar {ev.bar}: {ev.spec.note}
                  </li>
                ))}
            </ul>
          )}
          <div className="chips">
            {moment.paths.map((id) => {
              const path = pathById(id);
              return path ? (
                <a key={id} className="chip" href={`#path.${id}`}>
                  {path.name} →
                </a>
              ) : null;
            })}
            {moment.feelings.map((f) => (
              <a key={f} className="chip" href={`#feel.${f}`} style={{ fontStyle: 'italic' }}>
                {f.replace('-', ' ')}
              </a>
            ))}
          </div>
          <p className="editorial">Reading by this project, based on published analyses. Other readings are possible.</p>
        </section>
      )}
      {!p.flipped && depth >= 1 && (
        <p className="editorial">
          <a href={`#${piece.id}.${moment.id}`}>Link to this moment</a>
        </p>
      )}
    </article>
  );
}

/** A way into the tune room when Bach set this chorale's melody more than once. */
function TuneLink({ pieceId }: { pieceId: string }) {
  const id = corpusEntry(pieceId)?.derivedFrom ?? pieceId;
  const tune = CORPUS.tunes.find((t) => t.ids.includes(id) || t.variants.includes(id));
  if (!tune) return null;
  const others = tune.ids.length + tune.variants.length - 1;
  return (
    <a className="chip tune-link" href={`#tune.${tune.slug}`}>
      Same melody, {others} other {others === 1 ? 'setting' : 'settings'}: {TUNE_NOTES[tune.slug]?.name ?? tune.name} →
    </a>
  );
}

/** "How this page works", in four lines. */
function PageHelp({ onClose }: { onClose: () => void }) {
  return (
    <section className="page-help" aria-label="How this page works">
      <div className="panel-head">
        <strong>How this page works</strong>
        <button className="btn quiet icon" onClick={onClose} aria-label="Close help">
          <CloseIcon />
        </button>
      </div>
      <ol>
        <li>
          <b>Play</b> (or press space). The line shows the pull of the harmony: up when it pulls away from home, down when it rests.
        </li>
        <li>
          <b>Listen</b> keeps the page quiet while you hear it; <b>Map</b> shows the whole piece at once: keys, cadences and the patterns it uses.
        </li>
        <li>
          Click the line to jump there; drag across it to loop a passage. <b>Hear</b> lets you strip the music down to chords, melody and bass, or the bass alone.
        </li>
        <li>
          The card below follows the music. <b>What if…</b> changes one thing so you can hear what it did; <b>Guess what comes next</b> stops before a resolution.
          How many names you see is set by <b>Theory</b> at the top of the page.
        </li>
      </ol>
    </section>
  );
}

/** What the map draws, row by row. */
function MapLegend({ depth, sections }: { depth: number; sections: boolean }) {
  return (
    <details className="legend">
      <summary>What am I seeing?</summary>
      <dl>
        <div>
          <dt>
            <span className="legend-line" aria-hidden="true" /> The line
          </dt>
          <dd>The pull of the harmony at each moment. Rust at the top means pulling hard; teal at the bottom means at rest, at home.</dd>
        </div>
        {sections && (
          <div>
            <dt>Top band</dt>
            <dd>The form: the sections of the piece (theme, variations, exposition…).</dd>
          </div>
        )}
        <div>
          <dt>Shaded band</dt>
          <dd>Which key the music is in: {depth >= 2 ? 'the home key, other keys it visits, and the minor-mode shadows.' : 'at home, somewhere else, or in shadow (minor).'}</dd>
        </div>
        <div>
          <dt>Marks . ; , …</dt>
          <dd>Where phrases end, like punctuation: a full stop arrives home, a comma leaves a question open, … is a surprise turn.</dd>
        </div>
        <div>
          <dt>Boxes</dt>
          <dd>Patterns the music walks through (cadences, sequences, stock progressions). Click one to hear it on its own.</dd>
        </div>
        <div>
          <dt>Gravity well</dt>
          <dd>The current key seen from above: home in the centre, chords that pull hardest nearest to it. The lit chord is the one sounding.</dd>
        </div>
        <div>
          <dt>Voices</dt>
          <dd>Each line of the music as it moves; an orange note rubs against its chord.</dd>
        </div>
      </dl>
    </details>
  );
}

/** A small staff with a note: the icon of the moving score. */
function ScoreGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M2 6h20M2 10h20M2 14h20M2 18h20" opacity="0.55" />
      <ellipse cx="10" cy="16" rx="2.6" ry="1.9" transform="rotate(-20 10 16)" fill="currentColor" stroke="none" />
      <path d="M12.4 15.4V4.5" strokeWidth="1.8" />
    </svg>
  );
}
